// Thin fetch wrapper.
//
// Every call carries the session cookie. A 401 means signed out, which callers
// handle by showing the sign-in screen rather than treating it as a failure.
//
// The server returns one error shape for every failure:
//   { error: "<code>", message?: "<sentence>", details?: [{ field, message }] }
// ApiError carries all three so a caller can branch on the code, show the
// sentence, and mark individual form fields from the details.

export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message || code || `Request failed with ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

export async function api(path, options = {}) {
  // raw sends the body as given, for multipart uploads where the browser has
  // to set the Content-Type boundary itself.
  const { raw, ...rest } = options
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(rest.body && !raw ? { 'Content-Type': 'application/json' } : {}),
      ...rest.headers,
    },
    ...rest,
    body: rest.body && !raw ? JSON.stringify(rest.body) : rest.body,
  })

  const isJson = response.headers.get('content-type')?.includes('application/json')
  const payload = isJson ? await response.json().catch(() => null) : null

  if (!response.ok) {
    throw new ApiError(response.status, payload?.error, payload?.message, payload?.details)
  }
  return payload
}

export const getMe = () => api('/auth/me')
export const setViewAs = (role) => api('/auth/view-as', { method: 'POST', body: { role } })
export const logout = () => api('/auth/logout', { method: 'POST' })

export const listUsers = () => api('/api/users')
export const createUser = (user) => api('/api/users', { method: 'POST', body: user })
export const updateUser = (id, changes) =>
  api(`/api/users/${id}`, { method: 'PATCH', body: changes })
export const removeUser = (id) => api(`/api/users/${id}`, { method: 'DELETE' })
export const setOnsiteOpt = (franchiseeId, optIn) =>
  api(`/api/franchisees/${franchiseeId}/onsite-opt`, { method: 'POST', body: { optIn } })

export const getSettings = () => api('/api/settings')
export const saveSettings = (settings) => api('/api/settings', { method: 'PUT', body: settings })
export const sendMailTest = () => api('/api/settings/mail-test', { method: 'POST' })

export const listFranchisees = () => api('/api/franchisees')

// A path with its query string, blank and missing values left out.
const withParams = (path, params) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== '' && value != null),
  ).toString()
  return `${path}${query ? `?${query}` : ''}`
}
const withQuery = (path, params) => api(withParams(path, params))

export const listCustomers = (params = {}) => withQuery('/api/customers', params)

export const getCustomer = (id) => api(`/api/customers/${id}`)

// Creating a customer carries the agreement file, so it goes as multipart:
// the file plus the fields as one JSON part. The body is passed through
// untouched; setting a Content-Type here would strip the multipart boundary.
const multipart = (path, method, fields, file) => {
  const form = new FormData()
  form.append('payload', JSON.stringify(fields))
  if (file) form.append('agreement', file)
  return api(path, { method, body: form, raw: true })
}

export const createCustomer = (customer, agreementFile) =>
  multipart('/api/customers', 'POST', customer, agreementFile)
export const replaceAgreement = (customerId, agreementFile) =>
  multipart(`/api/customers/${customerId}/agreement`, 'PUT', {}, agreementFile)
export const agreementUrl = (customerId) => `/api/customers/${customerId}/agreement`
export const updateCustomer = (id, changes) =>
  api(`/api/customers/${id}`, { method: 'PATCH', body: changes })
export const removeCustomer = (id) => api(`/api/customers/${id}`, { method: 'DELETE' })
export const setCustomerSplit = (id, splitFranchiseePct) =>
  api(`/api/customers/${id}/split`, { method: 'PUT', body: { splitFranchiseePct } })

export const addContact = (customerId, contact) =>
  api(`/api/customers/${customerId}/contacts`, { method: 'POST', body: contact })
export const removeContact = (customerId, contactId) =>
  api(`/api/customers/${customerId}/contacts/${contactId}`, { method: 'DELETE' })

export const addSite = (customerId, site) =>
  api(`/api/customers/${customerId}/sites`, { method: 'POST', body: site })
export const removeSite = (customerId, siteId) =>
  api(`/api/customers/${customerId}/sites/${siteId}`, { method: 'DELETE' })

export const listTestTypes = () => api('/api/test-types')
export const createTestType = (testType) =>
  api('/api/test-types', { method: 'POST', body: testType })
export const updateTestType = (id, changes) =>
  api(`/api/test-types/${id}`, { method: 'PATCH', body: changes })
export const removeTestType = (id) => api(`/api/test-types/${id}`, { method: 'DELETE' })

export const getCustomerPricing = (customerId) => api(`/api/customers/${customerId}/pricing`)
export const addCustomerPricing = (customerId, row) =>
  api(`/api/customers/${customerId}/pricing`, { method: 'POST', body: row })
export const removeCustomerPricing = (customerId, rowId) =>
  api(`/api/customers/${customerId}/pricing/${rowId}`, { method: 'DELETE' })
export const getClinicBillRates = (customerId) => api(`/api/customers/${customerId}/bill-rates`)
export const addClinicBillRate = (customerId, row) =>
  api(`/api/customers/${customerId}/bill-rates`, { method: 'POST', body: row })
export const removeClinicBillRate = (customerId, rowId) =>
  api(`/api/customers/${customerId}/bill-rates/${rowId}`, { method: 'DELETE' })

// The activity dashboard and one customer's history, both filterable by
// franchisee.
export const getActivityOverview = (params = {}) => withQuery('/api/activity', params)
export const getCustomerActivity = (customerId, params = {}) =>
  withQuery(`/api/activity/customers/${customerId}`, params)

// The monthly import pipeline, admin only: create a batch with its files,
// parse one file per call, edit the drafts, commit, and delete.
export const listActivityImports = () => api('/api/activity/imports')
export const createActivityImport = (serviceMonth, files, source) => {
  const form = new FormData()
  form.append('serviceMonth', serviceMonth)
  form.append('source', source)
  for (const file of files) form.append('files', file)
  return api('/api/activity/imports', { method: 'POST', body: form, raw: true })
}
export const getActivityImport = (id) => api(`/api/activity/imports/${id}`)
export const parseNextImportFile = (id) =>
  api(`/api/activity/imports/${id}/parse-next`, { method: 'POST' })
export const updateImportLine = (importId, lineId, changes) =>
  api(`/api/activity/imports/${importId}/lines/${lineId}`, { method: 'PATCH', body: changes })
export const commitActivityImport = (id) =>
  api(`/api/activity/imports/${id}/commit`, { method: 'POST' })
export const removeActivityImport = (id) => api(`/api/activity/imports/${id}`, { method: 'DELETE' })
export const removeActivityRecord = (id) => api(`/api/activity/records/${id}`, { method: 'DELETE' })
// The bulk imports carry the spreadsheet itself, so they go as multipart,
// the file under one field named file.
const importFile = (path, file) => {
  const form = new FormData()
  form.append('file', file)
  return api(path, { method: 'POST', body: form, raw: true })
}

export const importOnsitesFile = (file) => importFile('/api/onsites/import', file)
export const importFranchiseesFile = (file) => importFile('/api/franchisees/import', file)
export const importUsersFile = (file) => importFile('/api/users/import', file)

// Public submission links. Management needs a session; the two public calls
// are made from the tokenized /submit page with no session at all.
export const listFormLinks = () => api('/api/form-links')
export const createFormLink = (link) => api('/api/form-links', { method: 'POST', body: link })
export const revokeFormLink = (id) => api(`/api/form-links/${id}/revoke`, { method: 'POST' })
export const getPublicForm = (token) => api(`/api/public/forms/${token}`)
export const submitPublicForm = (token, body) =>
  api(`/api/public/forms/${token}`, { method: 'POST', body })
export const listOnsites = (params = {}) => withQuery('/api/onsites', params)
export const getOnsite = (id) => api(`/api/onsites/${id}`)
export const createOnsite = (onsite) => api('/api/onsites', { method: 'POST', body: onsite })
export const updateOnsite = (id, changes) =>
  api(`/api/onsites/${id}`, { method: 'PATCH', body: changes })
export const scheduleOnsite = (id) => api(`/api/onsites/${id}/schedule`, { method: 'POST' })
export const completeOnsite = (id) => api(`/api/onsites/${id}/complete`, { method: 'POST' })
export const cancelOnsite = (id, reason) =>
  api(`/api/onsites/${id}/cancel`, { method: 'POST', body: { reason: reason ?? null } })
export const removeOnsite = (id) => api(`/api/onsites/${id}`, { method: 'DELETE' })
export const resendDispatchEmail = (id) =>
  api(`/api/onsites/${id}/dispatch-email`, { method: 'POST' })
// The completed on-site summary and its documents. The summary is the one
// onsite write a clinic holds, on its own jobs.
export const saveOnsiteSummary = (id, summary) =>
  api(`/api/onsites/${id}/summary`, { method: 'PUT', body: summary })
export const uploadOnsiteDocument = (id, file) => {
  const form = new FormData()
  form.append('document', file)
  return api(`/api/onsites/${id}/documents`, { method: 'POST', body: form, raw: true })
}
export const onsiteDocumentUrl = (id, documentId) => `/api/onsites/${id}/documents/${documentId}`
export const removeOnsiteDocument = (id, documentId) =>
  api(`/api/onsites/${id}/documents/${documentId}`, { method: 'DELETE' })

export const listIssues = (customerId) => api(`/api/customers/${customerId}/issues`)
export const addIssue = (customerId, issue) =>
  api(`/api/customers/${customerId}/issues`, { method: 'POST', body: issue })
export const resolveIssue = (customerId, issueId, body) =>
  api(`/api/customers/${customerId}/issues/${issueId}/resolve`, { method: 'POST', body })
export const removeIssue = (customerId, issueId) =>
  api(`/api/customers/${customerId}/issues/${issueId}`, { method: 'DELETE' })

export const getMonthlyReport = (month, source) =>
  withQuery('/api/reports/monthly', { month, source })
export const listAuditLog = (entity) => withQuery('/api/reports/audit', entity ? { entity } : {})
export const reportCsvUrl = (month, source) =>
  withParams('/api/reports/export.csv', { month, source })
export const reportPdfUrl = (month, source) =>
  withParams('/api/reports/monthly.pdf', { month, source })

export const createFranchisee = (franchisee) =>
  api('/api/franchisees', { method: 'POST', body: franchisee })
export const updateFranchisee = (id, changes) =>
  api(`/api/franchisees/${id}`, { method: 'PATCH', body: changes })
export const removeFranchisee = (id) => api(`/api/franchisees/${id}`, { method: 'DELETE' })
